// NPCInteraction Modal Component - TypeTown Bright Theme
// Awards credits when questions are typed correctly

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Star, Volume2 } from 'lucide-react';
import axios from 'axios';
import { NPC_DATA } from '../../data/npcData';
import { useTTS } from '../../hooks/useTTS';

const API = process.env.REACT_APP_BACKEND_URL;

const NPCInteraction = ({ npc, onClose, onCreditsEarned, voiceEnabled = true }) => {
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [typedText, setTypedText] = useState('');
  const [showResponse, setShowResponse] = useState(false);
  const [isTypingComplete, setIsTypingComplete] = useState(false);
  const [creditsAwarded, setCreditsAwarded] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const { speakAsNPC, stop: stopTTS, isPlaying } = useTTS();
  
  const npcData = NPC_DATA[npc] || NPC_DATA.farmer;
  
  // Speak NPC greeting when modal opens
  useEffect(() => {
    if (voiceEnabled && npcData.greeting) {
      speakAsNPC(npcData.greeting, npc);
    }
    return () => stopTTS();
  }, [npc, voiceEnabled]);
  
  // Focus the input when question is selected
  useEffect(() => { 
    if (selectedQuestion !== null && inputRef.current) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [selectedQuestion]);
  
  // Keep focus on input while typing
  const focusInput = useCallback(() => {
    if (selectedQuestion !== null && !showResponse && inputRef.current) {
      inputRef.current.focus();
    }
  }, [selectedQuestion, showResponse]);
  
  const currentQuestion = selectedQuestion !== null ? npcData.questions[selectedQuestion] : null;
  
  // Calculate accuracy
  const calculateAccuracy = useCallback((newTypedText) => {
    if (!currentQuestion) return 0;
    let correct = 0;
    for (let i = 0; i < newTypedText.length; i++) {
      if (newTypedText[i] === currentQuestion.q[i]) correct++;
    }
    return Math.round((correct / currentQuestion.q.length) * 100);
  }, [currentQuestion]);
  
  // Handle input change directly 
  const handleInputChange = async (e) => {
    if (!currentQuestion || showResponse || isTypingComplete) return;
    
    const newValue = e.target.value;
    if (newValue.length <= currentQuestion.q.length) {
      setTypedText(newValue);
      
      if (newValue.length >= currentQuestion.q.length) {
        setIsTypingComplete(true);
        const accuracy = calculateAccuracy(newValue);
        
        // Award 5 credits for any successful NPC conversation (80%+ accuracy)
        if (accuracy >= 80) {
          // Call API to save credits
          const token = localStorage.getItem('typetown_token');
          try {
            const res = await axios.post(`${API}/api/island/npc/conversation`, {
              npc_id: npc,
              question: currentQuestion.q,
              typed_text: newValue,
              accuracy: accuracy
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            if (res.data.success) {
              setCreditsAwarded(res.data.credits_earned);
              if (onCreditsEarned) {
                onCreditsEarned(res.data.credits_earned);
              }
            }
          } catch (e) {
            console.error('Failed to award credits:', e);
          }
        } else {
          setCreditsAwarded(0);
        }
        
        setTimeout(() => setShowResponse(true), 500);
      }
    }
  };
  
  const handleKeyDown = (e) => {
    if (!currentQuestion || showResponse) return;
    
    if (e.key === 'Escape') { 
      e.preventDefault();
      setSelectedQuestion(null);
      setTypedText('');
      return; 
    }
  };
  
  const resetInteraction = () => {
    setSelectedQuestion(null);
    setTypedText('');
    setShowResponse(false);
    setIsTypingComplete(false);
    setCreditsAwarded(0);
    stopTTS();
  };
  
  // Select a question and read it aloud
  const selectQuestion = (index) => {
    setSelectedQuestion(index);
    setTypedText('');
    setShowResponse(false);
    setIsTypingComplete(false);
    
    // Read the question aloud
    if (voiceEnabled && npcData.questions[index]) {
      setTimeout(() => {
        speakAsNPC(npcData.questions[index].q, npc);
      }, 300);
    }
  };
  
  // Read NPC response aloud
  useEffect(() => {
    if (showResponse && voiceEnabled && currentQuestion) {
      speakAsNPC(currentQuestion.a, npc);
    }
  }, [showResponse]);
  
  // NPC avatar colors based on their profession
  const npcAvatarColors = {
    farmer: { bg: 'from-emerald-400 to-green-500', border: 'border-emerald-300' },
    baker: { bg: 'from-amber-300 to-orange-400', border: 'border-amber-200' },
    teacher: { bg: 'from-indigo-400 to-purple-500', border: 'border-indigo-300' },
    doctor: { bg: 'from-sky-300 to-blue-400', border: 'border-sky-200' },
    artist: { bg: 'from-pink-400 to-rose-500', border: 'border-pink-300' },
    explorer: { bg: 'from-amber-500 to-yellow-600', border: 'border-amber-400' },
    chef: { bg: 'from-red-400 to-orange-500', border: 'border-red-300' },
    musician: { bg: 'from-purple-400 to-pink-500', border: 'border-purple-300' },
    librarian: { bg: 'from-indigo-300 to-blue-400', border: 'border-indigo-200' },
    gardener: { bg: 'from-green-400 to-emerald-500', border: 'border-green-300' },
    scientist: { bg: 'from-cyan-400 to-blue-500', border: 'border-cyan-300' },
    pilot: { bg: 'from-blue-400 to-indigo-500', border: 'border-blue-300' },
    firefighter: { bg: 'from-red-500 to-orange-600', border: 'border-red-400' },
    vet: { bg: 'from-teal-400 to-green-500', border: 'border-teal-300' },
    postie: { bg: 'from-red-400 to-rose-500', border: 'border-red-300' },
    police: { bg: 'from-blue-500 to-indigo-600', border: 'border-blue-400' },
    builder: { bg: 'from-orange-400 to-amber-500', border: 'border-orange-300' },
    dancer: { bg: 'from-pink-400 to-purple-500', border: 'border-pink-300' },
    athlete: { bg: 'from-green-500 to-teal-600', border: 'border-green-400' },
    astronaut: { bg: 'from-slate-400 to-gray-500', border: 'border-slate-300' },
    zookeeper: { bg: 'from-lime-400 to-green-500', border: 'border-lime-300' },
    magician: { bg: 'from-purple-500 to-violet-600', border: 'border-purple-400' },
  };
  const avatarStyle = npcAvatarColors[npc] || npcAvatarColors.farmer;
  
  // NPC emoji based on profession
  const npcEmoji = {
    farmer: '👨‍🌾', baker: '👩‍🍳', teacher: '👨‍🏫', doctor: '👨‍⚕️',
    artist: '🎨', explorer: '🧭', chef: '👨‍🍳', musician: '🎵',
    librarian: '📚', gardener: '🌻', scientist: '🔬', pilot: '✈️',
    firefighter: '🚒', vet: '🐾', postie: '📬', police: '👮',
    builder: '🔨', dancer: '💃', athlete: '🏃', astronaut: '🚀',
    zookeeper: '🦁', magician: '🎩'
  };
  
  return (
    <div className="fixed inset-0 bg-gradient-to-b from-sky-200/95 via-emerald-100/95 to-green-200/95 backdrop-blur-sm z-[300] flex items-center justify-center p-2 sm:p-4" data-testid="npc-interaction-modal">
      <div className="bg-white border-3 sm:border-4 border-mint-green rounded-2xl sm:rounded-3xl p-4 sm:p-6 max-w-lg w-full shadow-2xl shadow-mint-green/20 max-h-[95vh] overflow-y-auto">
        {/* NPC Header - Friendly TypeTown style */}
        <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6 pb-3 sm:pb-4 border-b-2 border-soft-gray">
          <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br ${avatarStyle.bg} flex items-center justify-center shadow-lg ${avatarStyle.border} border-2 sm:border-3`}>
            <span className="text-2xl sm:text-3xl">{npcEmoji[npc] || '👤'}</span>
          </div>
          <div className="flex-1">
            <h2 className="text-xl sm:text-2xl font-display font-bold text-text-dark">{npcData.name}</h2>
            <p className="text-text-medium text-sm sm:text-base">{npcData.greeting}</p>
          </div>
          <button 
            onClick={onClose} 
            className="w-8 h-8 sm:w-10 sm:h-10 bg-coral-orange/20 hover:bg-coral-orange/40 rounded-full flex items-center justify-center text-coral-orange text-lg sm:text-xl font-bold transition-all" 
            data-testid="npc-close-btn"
          >
            ✕
          </button>
        </div>
        
        {/* Question Selection */}
        {selectedQuestion === null && (
          <div>
            <p className="text-text-medium mb-4 text-center font-semibold">Choose a question to ask:</p>
            <div className="space-y-3 max-h-[50vh] overflow-y-auto">
              {npcData.questions.map((q, i) => (
                <button
                  key={i}
                  onClick={() => selectQuestion(i)}
                  className="w-full p-4 bg-gradient-to-r from-cream to-warm-white hover:from-sunny-yellow/20 hover:to-mint-green/20 border-2 border-soft-gray hover:border-sunny-yellow rounded-2xl text-left transition-all group shadow-sm"
                  data-testid={`npc-question-${i}`}
                >
                  <span className="text-sunny-yellow mr-2 text-lg">💬</span>
                  <span className="text-text-dark group-hover:text-text-dark font-medium">{q.q}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Typing the question */}
        {selectedQuestion !== null && !showResponse && (
          <div ref={containerRef} onClick={focusInput}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-text-medium text-sm font-semibold">Type the question to ask {npcData.name}:</p>
              {voiceEnabled && (
                <button
                  onClick={() => speakAsNPC(currentQuestion.q, npc)}
                  disabled={isPlaying}
                  className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold transition-colors ${
                    isPlaying ? 'bg-mint-green/20 text-mint-green' : 'bg-lavender/20 text-lavender hover:bg-lavender/30'
                  }`}
                >
                  <Volume2 className="w-3 h-3" />
                  {isPlaying ? 'Playing...' : 'Listen'}
                </button>
              )}
            </div>
            
            <div 
              className="bg-gradient-to-r from-cream to-warm-white rounded-2xl p-6 mb-4 border-2 border-sunny-yellow/30 cursor-text shadow-inner max-h-[40vh] overflow-y-auto"
              onClick={focusInput}
            >
              <p className="text-lg md:text-xl text-center font-mono tracking-wide leading-relaxed break-words whitespace-pre-wrap">
                {currentQuestion.q.split('').map((char, i) => (
                  <span key={i} className={
                    i < typedText.length 
                      ? (typedText[i] === char ? 'text-mint-green font-bold' : 'text-coral-orange bg-coral-orange/20 rounded') 
                      : i === typedText.length 
                        ? 'text-sunny-yellow bg-sunny-yellow/30 rounded animate-pulse font-bold' 
                        : 'text-text-medium/40'
                  }>
                    {char === ' ' ? '\u00A0' : char}
                  </span>
                ))}
              </p>
            </div>
            
            {/* Input field */}
            <input 
              ref={inputRef} 
              type="text" 
              value={typedText}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onBlur={() => setTimeout(focusInput, 50)}
              className="w-full px-4 py-3 bg-white border-2 border-lavender/50 rounded-2xl text-text-dark text-center font-mono text-lg focus:outline-none focus:border-lavender focus:ring-4 focus:ring-lavender/20 shadow-sm placeholder:text-text-medium/40"
              placeholder="Start typing here..."
              autoFocus
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              data-testid="npc-typing-input"
            />
            
            <div className="flex justify-between items-center mt-4 mb-4">
              <span className="text-text-medium font-medium">Progress: {typedText.length}/{currentQuestion.q.length}</span>
              <button 
                onClick={resetInteraction}
                className="text-coral-orange hover:text-coral-orange/80 text-sm font-semibold"
              >
                ← Choose different question
              </button>
            </div>
            
            <div className="h-3 bg-soft-gray rounded-full overflow-hidden shadow-inner">
              <div 
                className="h-full bg-gradient-to-r from-lavender to-mint-green transition-all rounded-full" 
                style={{ width: `${(typedText.length / currentQuestion.q.length) * 100}%` }} 
              />
            </div>
            
            {isTypingComplete && (
              <div className="mt-4 text-center text-mint-green animate-pulse font-semibold">
                ✓ Asking {npcData.name}...
              </div>
            )}
          </div>
        )}
        
        {/* NPC Response */}
        {showResponse && (
          <div className="animate-fadeIn">
            {/* Credits earned */}
            {creditsAwarded > 0 && (
              <div className="bg-gradient-to-r from-sunny-yellow/30 to-mint-green/30 border-2 border-sunny-yellow rounded-2xl p-4 mb-4 text-center animate-bounce">
                <p className="text-sunny-yellow text-xl font-display font-bold flex items-center justify-center gap-2">
                  <Star className="w-6 h-6" /> +{creditsAwarded} Credits Earned! <Star className="w-6 h-6" />
                </p>
                <p className="text-text-medium text-sm">Great typing!</p>
              </div>
            )}
            
            <div className="bg-gradient-to-r from-sunny-yellow/20 to-sunny-yellow/10 border-2 border-sunny-yellow/50 rounded-2xl p-4 mb-4">
              <p className="text-sunny-yellow font-semibold text-sm mb-1">You asked:</p>
              <p className="text-text-dark italic">"{currentQuestion.q}"</p>
            </div>
            
            <div className="bg-gradient-to-r from-mint-green/20 to-mint-green/10 border-2 border-mint-green/50 rounded-2xl p-4 mb-6">
              <p className="text-mint-green font-semibold text-sm mb-1">{npcData.name} says:</p>
              <p className="text-text-dark text-lg">"{currentQuestion.a}"</p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={resetInteraction}
                className="flex-1 py-3 bg-lavender/20 border-2 border-lavender rounded-2xl text-lavender hover:bg-lavender/30 transition-all font-semibold"
              >
                Ask another question
              </button>
              <button
                onClick={onClose}
                className="flex-1 py-3 bg-gradient-to-r from-mint-green to-sky-blue hover:from-sky-blue hover:to-mint-green rounded-2xl text-white font-bold transition-all shadow-md"
              >
                Say goodbye 👋
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NPCInteraction;
